import { z } from 'zod';
import { partySchema, targetSchema } from '../../common';
import { objectIdSchema } from '../../validation';

/**
 * Snapshot of a party on an `Invoice` (the buyer or the seller).
 *
 * Built on top of {@link partySchema} so the snapshot carries the
 * party's `roles[]` and `ids[]` for richer downstream resolution.
 *
 * ## `walletId` is deprecated
 *
 * Pre org-to-org refactor, every invoice party also referenced a
 * `Wallet._id` so the legacy capture flow could update wallet
 * balances. The new flow targets the bilateral `Account` collection
 * instead, so `walletId` is now **optional** and unused by any
 * code path that lands here.
 *
 * The field stays in the schema as `.optional()` purely so existing
 * `Invoice` docs in production still parse without a migration —
 * the user's "no migrations" rule on this refactor. New writes
 * should NOT set `walletId`.
 *
 * The cross-party financial relationship is now keyed via
 * `Invoice.accountId` → `Account._id` (see `AccountSchema`).
 */
/**
 * The invoice-beneficiary "target" enum. Payment / invoice code
 * historically dispatched on the 12-value legacy relation target
 * (`business_customer` / `carrier_company` / `individual_customer` /
 * `carrier_company_driver` / `freelancer_driver` / `driver` /
 * `super_admin_system`); the invoice fan-out tables and payment
 * decision rules are still keyed on that shape.
 *
 * Post Auth-v2 hard cutover we keep the broad union here — narrower
 * than the historical version (super-admin subvariants collapsed to
 * `super_admin`, `broker` collapsed into the org roles path via
 * `roles[]`) but still wider than the 7-value canonical
 * {@link targetSchema} so downstream `switch (beneficiary.target)`
 * tables in payment / invoice services keep exhaustively branching.
 */
export const invoiceBeneficiaryTargetSchema = z.union([
  targetSchema,
  z.enum([
    'business_customer',
    'carrier_company',
    'individual_customer',
    'carrier_company_driver',
    'freelancer_driver',
  ]),
]);
export type InvoiceBeneficiaryTarget = z.infer<typeof invoiceBeneficiaryTargetSchema>;

export const invoiceBeneficiarySchema = partySchema.extend({
  target: invoiceBeneficiaryTargetSchema,
  /**
   * @deprecated — points at the legacy `Wallet` collection, which is
   * parked for the future personal-wallet-cards feature. New code
   * MUST NOT read or write this field; resolve party financials via
   * `Invoice.accountId` instead.
   *
   * Kept optional so legacy docs still parse without a migration.
   */
  walletId: objectIdSchema.optional(),
  /** the user id if the party is an individual user such as individual customer or freelancer */
  userId: objectIdSchema.optional(),
});
export type InvoiceBeneficiary = z.infer<typeof invoiceBeneficiarySchema>;
